import React, { useState, useEffect, useRef } from 'react';
import './SkillsMatrix.css';

const SKILL_DATA = [
  {
    category: "APPLICATION_SEC",
    items: [
      { name: "OWASP Top 10", url: "https://github.com/Bhowal19/OWASP-Top-10.git" },
      { name: "Secure Coding (Python)", url: "https://wiki.python.org/moin/Security" },
      { name: "Threat Modeling", url: "https://owasp.org/www-community/Threat_Modeling" },
      { name: "__STRIDE", url: "https://learn.microsoft.com/en-us/azure/security/develop/threat-modeling-tool-threats" },
      { name: "__PASTA", url: "https://versprite.com/blog/what-is-pasta-threat-modeling/" },
      { name: "Secure Code Review", url: "https://owasp.org/www-project-code-review-guide/" }
    ]
  },
  {
    category: "CLOUD_SEC_COMPLIANCE",
    items: [
      { name: "GDPR | HIPAA | SOC 2", url: "https://www.complianceforge.com/faq/cybersecurity-and-data-protection/what-is-cybersecurity-compliance/" },
      { name: "NIST Privacy Framework", url: "https://www.nist.gov/privacy-framework" },
      { name: "CIS Benchmarks", url: "https://www.cisecurity.org/benchmark" },
      { name: "PCI DSS", url: "https://www.pcisecuritystandards.org/" }
    ]
  },
  {
    category: "DEVSECOPS",
    items: [
      { name: "Terraform (IAC)", url: "https://www.terraform.io/" },
      { name: "Secure CI/CD Pipelines", url: "https://owasp.org/www-project-top-10-ci-cd-security-risks/" },
      { name: "Git Source Control Sec", url: "https://docs.github.com/en/code-security" },
      { name: "Secure SDLC", url: "https://www.nist.gov/publications/secure-software-development-framework-ssdf-version-11-recommendations-mitigating" }
    ]
  },
  {
    category: "SOC_OPERATIONS",
    items: [
      { name: "SIEM Management", url: "https://www.splunk.com/en_us/data-insights/what-is-siem.html" },
      { name: "SOAR Automation", url: "https://www.paloaltonetworks.com/cyberpedia/what-is-soar" },
      { name: "Threat Intelligence", url: "https://www.crowdstrike.com/cybersecurity-101/threat-intelligence/" },
      { name: "Incident Response", url: "https://www.cisa.gov/incident-response" },
      { name: "Malware Analysis", url: "https://www.sans.org/blog/what-is-malware-analysis/" },
      { name: "Log Analysis", url: "https://www.crowdstrike.com/cybersecurity-101/observability/log-analysis/" }
    ]
  },
  {
    category: "VULN_MANAGEMENT",
    items: [
      { name: "Nessus / Qualys", url: "https://www.tenable.com/products/nessus" },
      { name: "Risk Assessment", url: "https://csrc.nist.gov/glossary/term/risk_assessment" },
      { name: "CVE Analysis", url: "https://cve.mitre.org/" },
      { name: "Patch Management", url: "https://csrc.nist.gov/glossary/term/patch_management" },
      { name: "Remediation Tracking", url: "https://www.rapid7.com/fundamentals/vulnerability-management-remediation/" },
      { name: "Security Reporting", url: "https://www.sans.org/white-papers/34320/" }
    ]
  },
  {
    category: "SECURITY_TESTING",
    items: [
      { name: "Wireshark (Net Analysis)", url: "https://www.wireshark.org/" },
      { name: "Web & Net Pentesting", url: "https://owasp.org/www-project-web-security-testing-guide/" },
      { name: "SCADA / OT Security", url: "https://www.cisa.gov/topics/industrial-control-systems" },
      { name: "Burp Suite", url: "https://portswigger.net/burp" }
    ]
  }
];

const SkillMatrixTerminal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const terminalRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); 
        }
      },
      { threshold: 0.2 } 
    );

    if (terminalRef.current) {
      observer.observe(terminalRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    // ADDED ID="skills" HERE SO HERO BUTTON CAN FIND IT
    <section className="kali-section" id="skills" ref={terminalRef}>
      
      {/* SEO HACK: Visually hidden heading for bots to identify this section */}
      <h2 style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: '0'
      }}>
        Cybersecurity Skill Matrix: AppSec, DevSecOps, and Cloud Security
      </h2>

      <div className={`kali-container ${isVisible ? 'animate-active' : ''}`}>
        
        {/* Terminal Window Header */}
        <div className="terminal-bar">
          <div className="terminal-controls">
            <div className="control close"></div>
            <div className="control minimize"></div>
            <div className="control maximize"></div>
          </div>
          <div className="terminal-title">root@kali: ~/skills-matrix</div>
        </div>

        {/* Terminal Body */}
        <div className="terminal-body">
          
          {/* Top Command Prompt */}
          <div className="command-line">
            <span className="prompt-user">┌──(root㉿kali)</span>
            <span className="prompt-symbol">-[</span>
            <span className="prompt-path">~/skills-matrix</span>
            <span className="prompt-symbol">]</span>
          </div>
          
          {/* Active Command with Typing Effect */}
          <div className="command-line">
            <span className="prompt-user">└─#</span>
            <span className={`command-text ${isVisible ? 'typing-command' : ''}`}> 
              ./display_skills.sh --interactive
            </span>
            {isVisible && <span className="cursor-block">█</span>}
          </div>

          {/* SIDE PROMPT / HELPER TEXT */}
          <div className={`command-comment ${isVisible ? 'fade-in-delayed' : ''}`}>
            # Click the skills below to view official documentation...
          </div>

          {/* The Output Grid */}
          <div className="skills-output">
            {SKILL_DATA.map((group, groupIndex) => (
              <div key={groupIndex} className="skill-column">
                <h3 className="dir-name">{group.category}/</h3>
                
                <ul className="file-list">
                  {group.items.map((skill, itemIndex) => {
                    const delay = 1000 + (groupIndex * 100) + (itemIndex * 50);
                    
                    return (
                      <li 
                        key={itemIndex} 
                        className="skill-item-wrapper"
                        style={{ animationDelay: `${delay}ms` }}
                      >
                        <a 
                          href={skill.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="executable-link"
                        >
                          {skill.name}*
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom Prompt (Waiting for next input) */}
          <div className={`command-line active-line ${isVisible ? 'fade-in-final' : ''}`}>
            <span className="prompt-user">┌──(root㉿kali)</span>
            <span className="prompt-symbol">-[</span>
            <span className="prompt-path">~/skills-matrix</span>
            <span className="prompt-symbol">]</span>
          </div>
          <div className={`command-line ${isVisible ? 'fade-in-final' : ''}`}>
            <span className="prompt-user">└─#</span>
            <span className="blinking-cursor">_</span>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SkillMatrixTerminal;